import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Alert / Callout
 *
 * Motion is built in and respects prefers-reduced-motion.
 */

export type AlertVariant = 'info' | 'success' | 'warning' | 'error'

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
	variant?: AlertVariant
	title?: string
	dismissible?: boolean
	onDismiss?: () => void
	border?: boolean
	compact?: boolean
}

const variantStyles: Record<AlertVariant, { container: string; icon: string }> = {
	info: { container: 'bg-info/5 text-info border-info/20', icon: 'text-info' },
	success: { container: 'bg-success/5 text-success border-success/20', icon: 'text-success' },
	warning: { container: 'bg-warning/5 text-warning border-warning/20', icon: 'text-warning' },
	error: { container: 'bg-error/5 text-error border-error/20', icon: 'text-error' },
}

const variantIcons: Record<AlertVariant, React.ReactNode> = {
	info: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>,
	success: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
	warning: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>,
	error: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>,
}

export function Alert({ variant = 'info', title, dismissible = false, onDismiss, border = false, compact = false, className, children, ...props }: AlertProps) {
	const [visible, setVisible] = React.useState(true)
	const styles = variantStyles[variant]
	const icon = variantIcons[variant]
	const handleDismiss = React.useCallback(() => {
		setVisible(false)
		setTimeout(() => onDismiss?.(), 200)
	}, [onDismiss])

	return (
		<AnimatePresence>
			{visible && (
				<motion.div role="alert" className={cn('rounded-lg border', styles.container, border && 'border-l-4', compact ? 'px-3 py-2' : 'px-4 py-3', className)} initial={{ opacity: 0, y: -8, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} exit={{ opacity: 0, y: -8, height: 0, marginTop: 0, marginBottom: 0 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>
					<div className="flex gap-3">
						<div className={cn('flex-shrink-0 mt-0.5', styles.icon)}>{icon}</div>
						<div className="flex-1 min-w-0">
							{title && <h5 className={cn('font-ui font-semibold', compact ? 'text-xs' : 'text-sm', 'mb-0.5')}>{title}</h5>}
							<div className={cn('font-ui', compact ? 'text-xs' : 'text-sm', !title && 'mt-0.5')}>{children}</div>
						</div>
						{dismissible && (
							<motion.button onClick={handleDismiss} className="flex-shrink-0 p-1 rounded-md transition-colors duration-fast opacity-50 hover:opacity-100" aria-label="Dismiss" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
							</motion.button>
						)}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
